import { useState } from 'react';
import { CONFIG, Dialog, api, useToast } from './_barrel.js';


        // =======================================================================
        // Client intake — login-less self-service link + review queue
        // =======================================================================
        function InviteClientDialog({ token, onClose, onSuccess }) {
            const [form, setForm] = useState({ subject_type: 'individual', client_tag: '', note: '', expires_days: 30 });
            const [busy, setBusy] = useState(false);
            const [error, setError] = useState(null);
            const [created, setCreated] = useState(null);
            const [copied, setCopied] = useState(false);
            const toast = useToast();

            const create = async (e) => {
                e.preventDefault();
                setBusy(true); setError(null);
                try {
                    const body = {
                        subject_type: form.subject_type,
                        client_tag: form.client_tag || null,
                        note: form.note || null,
                        expires_days: parseInt(form.expires_days, 10) || 30,
                    };
                    const res = await api('/api/v1/intake/invites', { method: 'POST', body: JSON.stringify(body) }, token);
                    setCreated(res);
                    onSuccess?.();
                } catch (err) { setError(err.message); }
                finally { setBusy(false); }
            };

            const copy = () => {
                if (!created?.url) return;
                navigator.clipboard?.writeText(created.url).then(() => {
                    setCopied(true); toast('Link copied'); setTimeout(() => setCopied(false), 1500);
                });
            };

            const downloadPack = async () => {
                try {
                    const resp = await fetch(`${CONFIG.apiBase}/api/v1/intake/pack`,
                        { headers: { 'Authorization': `Bearer ${token}` } });
                    if (!resp.ok) throw new Error('Pack not available (HTTP ' + resp.status + ')');
                    const blob = await resp.blob();
                    const url = URL.createObjectURL(blob);
                    const a = document.createElement('a');
                    a.href = url; a.download = 'Obsidian_Arc_Onboarding_Pack.pdf';
                    document.body.appendChild(a); a.click(); a.remove();
                    URL.revokeObjectURL(url);
                } catch (e) { toast('Pack download failed: ' + e.message); }
            };

            return (
                <Dialog onClose={onClose} title="Invite a client">
                    {!created ? (
                        <form onSubmit={create} className="space-y-4">
                            <div className="text-sm text-muted">
                                Generates a unique, login-less link. Send it to the client — or their ops manager to
                                complete on their behalf. Their submission lands in your review queue; nothing is
                                monitored until you approve it.
                            </div>
                            <div>
                                <label>Client type</label>
                                <select value={form.subject_type} onChange={e => setForm({ ...form, subject_type: e.target.value })}>
                                    <option value="individual">Private individual / family</option>
                                    <option value="corporate">Corporate / executive</option>
                                </select>
                            </div>
                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <label>Client tag (optional)</label>
                                    <input value={form.client_tag} onChange={e => setForm({ ...form, client_tag: e.target.value })}
                                           placeholder="e.g. Purple Protection — A1" />
                                </div>
                                <div>
                                    <label>Link expires in (days)</label>
                                    <input type="number" min="1" max="180" value={form.expires_days}
                                           onChange={e => setForm({ ...form, expires_days: e.target.value })} />
                                </div>
                            </div>
                            <div>
                                <label>Internal note (optional)</label>
                                <input value={form.note} onChange={e => setForm({ ...form, note: e.target.value })}
                                       placeholder="Who this is for / how it's being sent" />
                            </div>
                            {error && <div className="text-[var(--red)] text-sm">{error}</div>}
                            <div className="flex justify-between items-center gap-2">
                                <button type="button" className="btn-ghost" onClick={downloadPack}
                                        title="The sendable PDF pack — attach it to an email for clients who prefer paper">
                                    ↓ Onboarding pack (PDF)
                                </button>
                                <div className="flex gap-2">
                                    <button type="button" className="btn-ghost" onClick={onClose}>Cancel</button>
                                    <button type="submit" className="btn-gold" disabled={busy}>{busy ? 'Creating…' : 'Create link'}</button>
                                </div>
                            </div>
                        </form>
                    ) : (
                        <div className="space-y-4">
                            <div className="text-sm">Share this secure link with your client:</div>
                            <div className="panel p-3 flex items-center gap-3" style={{ wordBreak: 'break-all' }}>
                                <span className="font-mono text-sm flex-1">{created.url}</span>
                                <button className="btn-gold" onClick={copy}>{copied ? 'Copied ✓' : 'Copy'}</button>
                            </div>
                            <div className="text-xs text-muted">
                                Single-use and expires {created.expires_at ? new Date(created.expires_at).toLocaleDateString() : 'soon'}.
                                The link is shown once — copy it now. It appears in your review queue as “Awaiting link”.
                            </div>
                            <div className="flex items-center gap-3">
                                <button type="button" className="btn-ghost" onClick={downloadPack}>↓ Onboarding pack (PDF)</button>
                                <span className="text-xs text-muted">Sending by email? Attach the pack for clients who prefer paper —
                                    they can also download it from their link.</span>
                            </div>
                            <div className="flex justify-end">
                                <button className="btn-ghost" onClick={onClose}>Done</button>
                            </div>
                        </div>
                    )}
                </Dialog>
            );
        }

export { InviteClientDialog };
